import { useCallback, useState } from 'react';

import {
    DEFAULT_IMPORT_EVENT_PLAN_TYPE,
    type ImportEventPlanType,
    type ImportEventSourceCategory,
} from '../../../../config/importEventFromPlanConfig';

/**
 * Transient state of the Import-Events modal: which plan list is in scope, which
 * plan is selected, the two search strings, and the picked promos.
 *
 * The reset cascades live here because they are the fiddly part. Every row on
 * screen belongs to the selected source plan, so anything that changes which
 * plan is in scope — the TPM/TPO switch, the source category, the plan itself —
 * has to drop the promos picked from the previous one, otherwise an import would
 * post promo ids the new source plan does not own.
 *
 * `defaultPlanType` is the tenant-configured side of the TPM/TPO switch; both the
 * first render and every reset land on it, so a tenant whose plans live on one
 * side never opens the modal on an empty list.
 */
export function useImportEventsForm(defaultPlanType: ImportEventPlanType = DEFAULT_IMPORT_EVENT_PLAN_TYPE) {
    const defaultIsTpmPlan = defaultPlanType === 'tpm';

    const [isTpmPlan, setIsTpmPlan] = useState(defaultIsTpmPlan);
    const [selectedSource, setSelectedSource] = useState<ImportEventSourceCategory>('self');
    const [sourcePlanId, setSourcePlanId] = useState<string | null>(null);
    const [planSearch, setPlanSearch] = useState('');
    const [eventSearch, setEventSearch] = useState('');
    const [eventSearchKeyword, setEventSearchKeyword] = useState('');
    const [selectedPromoIds, setSelectedPromoIds] = useState<string[]>([]);

    const clearEventSelection = useCallback(() => {
        setSelectedPromoIds([]);
        setEventSearch('');
        setEventSearchKeyword('');
    }, []);

    // Switching TPM/TPO swaps the whole plan list, so the previously chosen
    // category may not even exist in the new one — fall back to `self`.
    const changePlanType = useCallback(
        (nextIsTpmPlan: boolean) => {
            setIsTpmPlan(nextIsTpmPlan);
            setSelectedSource('self');
            setSourcePlanId(null);
            setPlanSearch('');
            clearEventSelection();
        },
        [clearEventSelection],
    );

    const changeSource = useCallback(
        (category: ImportEventSourceCategory) => {
            setSelectedSource(category);
            setSourcePlanId(null);
            clearEventSelection();
        },
        [clearEventSelection],
    );

    const changePlan = useCallback(
        (planId: string) => {
            setSourcePlanId(planId);
            clearEventSelection();
        },
        [clearEventSelection],
    );

    /** AntD's search input hands back the submitted value, so no state read is needed. */
    const submitEventSearch = useCallback((value: string) => setEventSearchKeyword(value), []);

    const reset = useCallback(() => {
        setIsTpmPlan(defaultIsTpmPlan);
        setSelectedSource('self');
        setSourcePlanId(null);
        setPlanSearch('');
        clearEventSelection();
    }, [defaultIsTpmPlan, clearEventSelection]);

    return {
        isTpmPlan,
        selectedSource,
        sourcePlanId,
        planSearch,
        eventSearch,
        eventSearchKeyword,
        selectedPromoIds,
        setPlanSearch,
        setEventSearch,
        setSelectedPromoIds,
        changePlanType,
        changeSource,
        changePlan,
        submitEventSearch,
        reset,
    };
}
